<script setup>
import { useQuasar } from 'quasar';
import { computed, ref, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * Responsive dialog with sm/md/lg size tiers; expands to full-screen on mobile.
 *
 * @status internal
 * @group cards-dialogs
 * @see https://quasar.dev/vue-components/dialog Quasar q-dialog API
 */
defineOptions({ name: 'HrStandardDialog', inheritAttrs: false });

const props = defineProps({
  /**
   * Max-width tier on tablet and desktop. Always full-screen on mobile.
   * @values sm, md, lg
   */
  size: {
    type: String,
    default: 'sm',
    validator: (v) => ['sm', 'md', 'lg'].includes(v),
  },
  /**
   * Prevents dismissal via backdrop click or Escape key.
   */
  persistent: {
    type: Boolean,
    default: false,
  },
  /**
   * Expands the dialog to full-screen on tablet and desktop. Mobile is always full-screen.
   */
  maximized: {
    type: Boolean,
    default: false,
  },
  /**
   * Prevents returning focus to the element that opened the dialog when it closes. Set when the
   * opener is a keyboard-activatable control (e.g. a clickable list row) that would otherwise
   * re-fire the closing keypress and immediately reopen the dialog.
   */
  noRefocus: {
    type: Boolean,
    default: false,
  },
  /**
   * Lets Escape reach the caller. The dialog never hides itself on it: it emits `escapeKey` and
   * the caller hides it once it has decided, so a form whose work would be lost can ask first.
   */
  escDismiss: {
    type: Boolean,
    default: false,
  },
  /**
   * Legacy props for backwards compatibility with the legacy theme.
   * @deprecated Only for backwards compatibility — remove once legacy consumers are migrated.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits({
  /** Escape was pressed while this dialog was the topmost layer. Only fired with `escDismiss`. */
  escapeKey: null,
});

const $q = useQuasar();
const attrs = useAttrs();
const { hasTheme } = useTheme();

const isMobile = computed(() => $q.platform.is.mobile);
const isFullScreen = computed(() => isMobile.value || props.maximized);

/*
 * The model is this component's to own: it is what turns Quasar's internal hide into a request
 * that can be refused, so a caller binding one of their own would take the dialog back and undo
 * the escape guard with no sign of having done so. Dropped from what is forwarded rather than
 * merely overridden further down the tag, because a hyphenated binding and a camel-cased one are
 * two different keys and which of them q-dialog reads is not ours to depend on.
 */
const MODEL_KEYS = ['modelValue', 'model-value', 'onUpdate:modelValue'];

const dialogProps = computed(() => {
  const base = {
    ...attrs,
    maximized: isFullScreen.value,
    persistent: props.persistent,
    noRefocus: props.noRefocus,
    noEscDismiss: props.escDismiss === false,
  };
  const forwarded = hasTheme.value ? base : { ...props.legacyProps, ...base };

  // Filtered once everything is merged, so the legacy channel cannot carry a model in either.
  return Object.fromEntries(
    Object.entries(forwarded).filter(([key]) => MODEL_KEYS.includes(key) === false),
  );
});

/*
 * Driven by a controlled v-model rather than QDialog's own show/hide, because a model the parent
 * owns turns Quasar's internal hide into a request: it emits the change and waits to be given it.
 * That split is what lets an Escape be guarded — Quasar still decides whether this dialog is the
 * topmost layer, since only the topmost gets the key at all, and the caller decides whether it
 * may go. Reaching for Quasar's own handler stack instead is not open to us: the module holding it
 * is private and the package resolves to a prebuilt bundle, so importing it would build a second
 * stack beside the one QMenu and QDialog share, and every layer would answer the same press.
 */
const isShowing = ref(false);

let isEscaping = false;

function onEscapeKey() {
  isEscaping = true;
}

/*
 * Every request is honoured except the one the caller asked to guard. The two arrive together and
 * in this order, Quasar emitting escapeKey immediately before it asks to hide.
 */
function onToggleRequest(value) {
  if (value === false && isEscaping === true) {
    isEscaping = false;
    emit('escapeKey');
    return;
  }
  isShowing.value = value;
}

// Exposed so consumers can use ref="dialogRef" from useDialogPluginComponent() and Quasar's dialog
// plugin can call show() on this component instance.
defineExpose({
  /** Opens the dialog. */
  show: () => { isShowing.value = true; },
  /** Closes the dialog. Dismisses outright, so a caller guarding Escape runs its guard first. */
  hide: () => { isShowing.value = false; },
});
</script>

<template>
  <q-dialog
    v-bind="dialogProps"
    :model-value="isShowing"
    @update:model-value="onToggleRequest"
    @escape-key="onEscapeKey"
  >
    <div :class="['hr-standard-dialog', !isFullScreen && `hr-standard-dialog--${size}`]">
      <!-- @slot default Dialog content — typically a card with sections and actions. -->
      <slot />
    </div>
  </q-dialog>
</template>

<style lang="scss" scoped>
.hr-standard-dialog {
  width: 100%;
  overflow: auto;
}

.hr-standard-dialog--sm {
  max-width: 560px;
  max-height: 90vh;
}

.hr-standard-dialog--md {
  max-width: 720px;
  max-height: 90vh;
}

.hr-standard-dialog--lg {
  max-width: 960px;
  max-height: 90vh;
}
</style>
